import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type ControllerRenderProps } from "react-hook-form";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@wellcheck/components/ui/form";

import { PeriodRangePicker } from "./PeriodRangePicker";

import { minStartDate } from "@/features/upload/data/minStartDate";
import { periodInfoSchema, type PeriodInfoInput } from "@/features/upload/data/periodInfoSchema";

export interface Step3PeriodInfoProps {
  defaultValues?: Partial<PeriodInfoInput>;
  onValidSubmit: (values: PeriodInfoInput) => void;
  /**
   * Wizard 下部の「次へ」から submit したいので form id を渡す。
   */
  formId: string;
}

/**
 * Step3PeriodInfo — 実施情報の入力フォーム。
 *
 * 表 ①〜⑧ のうち、画面に出すのは ③実施期間 / ⑧特記事項 のみ。
 *  - ①企業名: ページヘッダ (UploadPage) で URL の customerId 経由で表示済み
 *  - ②配付方法: mock では WEB:メール配信 固定 (人事担当に問う必要なし)
 *  - ④事前周知メール: 「事前周知は廃止」方針のため非表示
 *  - ⑤未受検者リスト / ⑥勧奨メール: 「要」固定 + メール文も周知メールと同じ固定
 *  - ⑦延長期間: 終了日から 2 週間で固定
 *
 * これらの固定挙動は将来 ops 側 (運営者) の設定で上書き可能にする想定。
 */
export function Step3PeriodInfo({ defaultValues, onValidSubmit, formId }: Step3PeriodInfoProps) {
  const form = useForm<PeriodInfoInput>({
    resolver: zodResolver(periodInfoSchema),
    mode: "onBlur",
    defaultValues: {
      periodStart: defaultValues?.periodStart ?? "",
      periodEnd: defaultValues?.periodEnd ?? "",
      notes: defaultValues?.notes ?? "",
    },
  });

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onValidSubmit)}
        className="space-y-6"
        noValidate
      >
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">③ 実施期間</legend>
          <p className="text-sm text-muted-foreground">
            カレンダーから開始日と終了日を選択してください (開始日は 8 日後以降・最大 1 ヶ月)
          </p>
          <PeriodRangePicker
            startIso={form.watch("periodStart")}
            endIso={form.watch("periodEnd")}
            minStart={minStartDate(new Date())}
            invalid={Boolean(form.formState.errors.periodStart || form.formState.errors.periodEnd)}
            onChange={({ startIso, endIso }) => {
              form.setValue("periodStart", startIso, { shouldValidate: true, shouldDirty: true });
              form.setValue("periodEnd", endIso, { shouldValidate: true, shouldDirty: true });
            }}
          />
          {(form.formState.errors.periodStart || form.formState.errors.periodEnd) && (
            <p className="text-sm text-destructive">
              {form.formState.errors.periodEnd?.message ??
                form.formState.errors.periodStart?.message}
            </p>
          )}
        </fieldset>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }: { field: ControllerRenderProps<PeriodInfoInput, "notes"> }) => (
            <FormItem>
              <FormLabel>⑧ その他特記事項</FormLabel>
              <FormControl>
                <textarea
                  {...field}
                  rows={4}
                  maxLength={500}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20"
                  placeholder="例: 9/20〜9/22 は社内研修のため回答が遅れる可能性があります"
                />
              </FormControl>
              <FormDescription>
                <span className="block">任意・500 文字以内</span>
                <span className="block">
                  従業員個人に関すること (氏名や健康状態など)
                  は書かず、ウエルフルジャパンへ直接ご連絡ください
                </span>
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
